import React from 'react';
import { useTheme } from '@mui/material/styles';

import { Box, Stack, Typography } from 'common/components/material';
import { DrawerHeader } from 'common/components/material/Drawer';
import { CloseButton } from 'common/components/navigation';
import { useDrawer } from 'common/hooks';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';

import { useGetOrganizationObjectives } from '../hooks/useGetOrganizationObjectives';
import { useGetTeamObjectives } from '../hooks/useGetTeamObjectives';
import { useObjectiveFilters } from '../hooks/useObjectiveFilters';
import { useObjectivesForView } from '../hooks/useObjectivesForView';
import ObjectiveFilterPillRow from './objective-filter-pill-row.component';
import ObjectiveList from './objective-list.component';
import { ObjectivesViewEnum } from '../objectives.constants';

export enum ObjectiveListSourceEnum {
  INDIVIDUAL = 'individual',
  REPORTEES = 'reportees',
  ORGANIZATION = 'organization',
  TEAM = 'team',
}
export type ObjectiveListDrawerProps = {
  source: ObjectiveListSourceEnum;
  filterKeys?: string[];
};

export const INIT_PROPS: ObjectiveListDrawerProps = {
  source: ObjectiveListSourceEnum.INDIVIDUAL,
  filterKeys: [],
};

const DRAWER_COPY = new Map<
  ObjectiveListSourceEnum,
  { title: string; subtitle: string; emptyMessage: string }
>([
  [
    ObjectiveListSourceEnum.INDIVIDUAL,
    {
      title: 'My objectives',
      subtitle: 'Everything you own this cycle',
      emptyMessage: 'No objectives yet.',
    },
  ],
  [
    ObjectiveListSourceEnum.REPORTEES,
    {
      title: "Team members' objectives",
      subtitle: 'Everything your reportees own this cycle',
      emptyMessage: 'No team member objectives yet.',
    },
  ],
  [
    ObjectiveListSourceEnum.ORGANIZATION,
    {
      title: 'Organizational objectives',
      subtitle: 'What the organization is driving this cycle',
      emptyMessage: 'No organizational objectives yet.',
    },
  ],
  [
    ObjectiveListSourceEnum.TEAM,
    {
      title: 'Team objectives',
      subtitle: 'What your team is driving this cycle',
      emptyMessage: 'No team objectives yet.',
    },
  ],
]);

const ObjectiveListDrawer: React.FC<ObjectiveListDrawerProps> = ({
  source = ObjectiveListSourceEnum.INDIVIDUAL,
  filterKeys = [],
}) => {
  const { palette } = useTheme();
  const { closeDrawer } = useDrawer();

  const isOwnedList =
    source === ObjectiveListSourceEnum.INDIVIDUAL ||
    source === ObjectiveListSourceEnum.REPORTEES;

  const { objectives: ownedObjectives, isLoading: isLoadingOwnedObjectives } =
    useObjectivesForView(
      source === ObjectiveListSourceEnum.REPORTEES
        ? ObjectivesViewEnum.TEAM
        : ObjectivesViewEnum.INDIVIDUAL,
    );
  const { data: organizationObjectives, isLoading: isLoadingOrganizationObjectives } =
    useGetOrganizationObjectives();
  const { data: teamObjectives, isLoading: isLoadingTeamObjectives } = useGetTeamObjectives();

  const objectivesBySource = {
    [ObjectiveListSourceEnum.INDIVIDUAL]: ownedObjectives,
    [ObjectiveListSourceEnum.REPORTEES]: ownedObjectives,
    [ObjectiveListSourceEnum.ORGANIZATION]: organizationObjectives,
    [ObjectiveListSourceEnum.TEAM]: teamObjectives,
  };
  const loadingBySource = {
    [ObjectiveListSourceEnum.INDIVIDUAL]: isLoadingOwnedObjectives,
    [ObjectiveListSourceEnum.REPORTEES]: isLoadingOwnedObjectives,
    [ObjectiveListSourceEnum.ORGANIZATION]: isLoadingOrganizationObjectives,
    [ObjectiveListSourceEnum.TEAM]: isLoadingTeamObjectives,
  };

  const objectives = objectivesBySource[source];
  const isLoading = loadingBySource[source];

  const { filterOptions, selectedKeys, toggleFilterKey, filteredObjectives } =
    useObjectiveFilters(objectives, filterKeys);

  const { title, subtitle, emptyMessage } = DRAWER_COPY.get(source)!;
  const close = () => closeDrawer(DrawerIdEnum.OBJECTIVE_LIST);

  return (
    <>
      <DrawerHeader sx={{ display: 'block', flexShrink: 0, p: 2, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="body2" fontWeight={600} color={palette.Text.Headline}>
              {title}
            </Typography>
            <Typography variant="caption" color={palette.Text.Caption}>
              {isLoading ? subtitle : `${objectives?.length ?? 0} · ${subtitle}`}
            </Typography>
          </Box>

          <CloseButton clickHandler={close} />
        </Stack>

        {!isLoading && filterOptions.length > 1 && (
          <Box mt={1.5}>
            <ObjectiveFilterPillRow
              options={filterOptions}
              selectedKeys={selectedKeys}
              onToggle={toggleFilterKey}
            />
          </Box>
        )}
      </DrawerHeader>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
        <ObjectiveList
          objectives={filteredObjectives}
          isLoading={isLoading}
          emptyMessage={
            objectives?.length ? 'No objectives match this filter.' : emptyMessage
          }
          showCategoryAndType={isOwnedList}
          showOwner={source !== ObjectiveListSourceEnum.INDIVIDUAL}
          readOnly={!isOwnedList}
          onNavigate={close}
        />
      </Box>
    </>
  );
};

export default ObjectiveListDrawer;
